/* global BigInt */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWeb3 } from '../context/Web3Context';
import { DURATIONS } from '../config/contracts';
import {
  formatUSDC,
  formatAPY,
  getOpportunityStatus,
  mapOpportunity,
  generateOptionLabels
} from '../utils/helpers';
import StatusBadge from '../components/StatusBadge';
import './Admin.css';

function Admin() {
  const { address, provider, marketContract } = useWeb3();
  const [searchParams, setSearchParams] = useSearchParams();

  const [hasListerRole, setHasListerRole] = useState(false);
  const [hasOracleRole, setHasOracleRole] = useState(false);
  const [loading, setLoading] = useState(true);
  const [opportunities, setOpportunities] = useState([]);
  const [currentBlock, setCurrentBlock] = useState(null);

  const CHAINS = {
    base: { name: 'Base', secsPerBlock: '2' },
    hyperevm: { name: 'HyperEVM', secsPerBlock: '1' }
  };

  // Form state - initialize from URL params if present
  const [formData, setFormData] = useState(() => {
    const chain = searchParams.get('chain') || 'base';
    return {
      name: searchParams.get('name') || '',
      chain: chain,
      secsPerBlock: CHAINS[chain]?.secsPerBlock || '2',
      duration: searchParams.get('duration') || '0',
      startBlock: '',
      refTxnHash: '',
      startApy: searchParams.get('startApy') || '',
      threshold1: searchParams.get('t1') || '-20',
      threshold2: searchParams.get('t2') || '-10',
      threshold3: searchParams.get('t3') || '0',
      threshold4: searchParams.get('t4') || '10',
      threshold5: searchParams.get('t5') || '20'
    };
  });

  // Clear URL params after they've been applied
  useEffect(() => {
    if (searchParams.has('name') || searchParams.has('chain') || searchParams.has('startApy')) {
      // Clear the params from URL without triggering navigation
      setSearchParams({}, { replace: true });
    }
  }, []);

  const handleChainChange = (e) => {
    const chain = e.target.value;
    setFormData(prev => ({
      ...prev,
      chain,
      secsPerBlock: CHAINS[chain]?.secsPerBlock || '2'
    }));
  };
  const [creating, setCreating] = useState(false);
  const [createMessage, setCreateMessage] = useState(null);

  // Resolution state
  const [resolvingId, setResolvingId] = useState(null);
  const [voidingId, setVoidingId] = useState(null);
  const [finalApyInputs, setFinalApyInputs] = useState({});

  const checkRoles = useCallback(async () => {
    if (!marketContract || !address) {
      setLoading(false);
      return;
    }

    try {
      const listerRole = await marketContract.LISTER_ROLE();
      const oracleRole = await marketContract.ORACLE_ROLE();
      const hasLister = await marketContract.hasRole(listerRole, address);
      const hasOracle = await marketContract.hasRole(oracleRole, address);

      setHasListerRole(hasLister);
      setHasOracleRole(hasOracle);
      setLoading(false);

      if (hasLister || hasOracle) {
        loadOpportunities();
      }
    } catch (error) {
      console.error('Error checking roles:', error);
      setLoading(false);
    }
  }, [marketContract, address]);

  const loadOpportunities = useCallback(async () => {
    if (!marketContract) return;

    try {
      if (provider) {
        const block = await provider.getBlockNumber();
        setCurrentBlock(block);
      }

      const count = await marketContract.getOpportunityCount();
      if (count === 0n) {
        setOpportunities([]);
        return;
      }

      const rawOpps = await marketContract.getOpportunitiesRange(0, count);
      const opps = rawOpps.map((opp, index) => {
        if (!opp.name) return null;
        return mapOpportunity(opp, index);
      }).filter(o => o !== null);

      setOpportunities(opps);
    } catch (error) {
      console.error('Error loading opportunities:', error);
    }
  }, [marketContract, provider]);

  useEffect(() => {
    checkRoles();
  }, [checkRoles]);

  useEffect(() => {
    if (hasListerRole || hasOracleRole) {
      const interval = setInterval(loadOpportunities, 30000);
      return () => clearInterval(interval);
    }
  }, [hasListerRole, hasOracleRole, loadOpportunities]);

  const fetchCurrentBlock = async () => {
    if (provider) {
      const block = await provider.getBlockNumber();
      setCurrentBlock(block);
      return block;
    }
    return null;
  };

  const setToNow = async () => {
    const block = await fetchCurrentBlock();
    if (block) {
      setFormData(prev => ({ ...prev, startBlock: block.toString() }));
    }
  };

  const addHour = async () => {
    const secsPerBlock = parseFloat(formData.secsPerBlock);
    if (!secsPerBlock) return;

    let base = parseInt(formData.startBlock);
    if (isNaN(base)) {
      const block = await fetchCurrentBlock();
      if (!block) return;
      base = block;
    }

    const blocksInHour = Math.floor(3600 / secsPerBlock);
    setFormData(prev => ({ ...prev, startBlock: (base + blocksInHour).toString() }));
  };

  const addDay = async () => {
    const secsPerBlock = parseFloat(formData.secsPerBlock);
    if (!secsPerBlock) return;

    let base = parseInt(formData.startBlock);
    if (isNaN(base)) {
      const block = await fetchCurrentBlock();
      if (!block) return;
      base = block;
    }

    const blocksInDay = Math.floor(86400 / secsPerBlock);
    setFormData(prev => ({ ...prev, startBlock: (base + blocksInDay).toString() }));
  };

  const calculateEndBlock = () => {
    const startBlock = parseInt(formData.startBlock);
    const secsPerBlock = parseFloat(formData.secsPerBlock);
    const duration = parseInt(formData.duration);

    if (isNaN(startBlock) || isNaN(secsPerBlock) || secsPerBlock <= 0) return '';

    let durationSeconds;
    if (duration === 0) durationSeconds = 86400;
    else if (duration === 1) durationSeconds = 604800;
    else durationSeconds = 2592000;

    const blocksNeeded = Math.floor(durationSeconds / secsPerBlock);
    return (startBlock + blocksNeeded).toString();
  };

  const createOpportunity = async (e) => {
    e.preventDefault();
    if (!marketContract) return;

    setCreating(true);
    setCreateMessage({ type: 'info', text: 'Creating opportunity... Please confirm in wallet.' });

    try {
      const startBlock = BigInt(formData.startBlock);
      const endBlock = BigInt(calculateEndBlock());
      const secsPerBlockFixed = BigInt(Math.floor(parseFloat(formData.secsPerBlock) * 1e18));
      const apyUnits = BigInt(Math.floor(parseFloat(formData.startApy) * 10000));

      const thresholds = {
        threshold1: BigInt(Math.floor(parseFloat(formData.threshold1) * 10000)),
        threshold2: BigInt(Math.floor(parseFloat(formData.threshold2) * 10000)),
        threshold3: BigInt(Math.floor(parseFloat(formData.threshold3) * 10000)),
        threshold4: BigInt(Math.floor(parseFloat(formData.threshold4) * 10000)),
        threshold5: BigInt(Math.floor(parseFloat(formData.threshold5) * 10000))
      };

      const tx = await marketContract.createOpportunity(
        formData.name,
        startBlock,
        endBlock,
        secsPerBlockFixed,
        formData.refTxnHash,
        parseInt(formData.duration),
        apyUnits,
        thresholds
      );

      setCreateMessage({ type: 'info', text: 'Waiting for confirmation...' });
      await tx.wait();

      setCreateMessage({ type: 'success', text: 'Opportunity created successfully!' });
      setFormData({
        name: '',
        chain: 'base',
        secsPerBlock: '2',
        duration: '0',
        startBlock: '',
        refTxnHash: '',
        startApy: '',
        threshold1: '-20',
        threshold2: '-10',
        threshold3: '0',
        threshold4: '10',
        threshold5: '20'
      });

      setTimeout(() => {
        setCreateMessage(null);
        loadOpportunities();
      }, 2000);
    } catch (error) {
      console.error('Error creating opportunity:', error);
      setCreateMessage({ type: 'error', text: `Error: ${error.message}` });
    } finally {
      setCreating(false);
    }
  };

  const resolveOpportunity = async (oppId) => {
    const finalApy = finalApyInputs[oppId];
    if (!finalApy) {
      alert('Please enter final APY');
      return;
    }

    setResolvingId(oppId);
    try {
      const finalApyUnits = BigInt(Math.floor(parseFloat(finalApy) * 10000));
      const tx = await marketContract.resolveOpportunity(oppId, finalApyUnits);
      await tx.wait();
      loadOpportunities();
    } catch (error) {
      console.error('Error resolving:', error);
      alert('Error: ' + error.message);
    } finally {
      setResolvingId(null);
    }
  };

  const voidOpportunity = async (oppId) => {
    if (!window.confirm('Are you sure you want to void this opportunity? All users will get refunds.')) {
      return;
    }

    setVoidingId(oppId);
    try {
      const tx = await marketContract.voidOpportunity(oppId);
      await tx.wait();
      loadOpportunities();
    } catch (error) {
      console.error('Error voiding:', error);
      alert('Error: ' + error.message);
    } finally {
      setVoidingId(null);
    }
  };

  if (!address) {
    return (
      <div className="connect-prompt">
        <h2>Connect Your Wallet</h2>
        <p>Please connect your wallet to access the admin panel.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading admin panel...</div>;
  }

  if (!hasListerRole && !hasOracleRole) {
    return (
      <div className="connect-prompt">
        <h2>Access Denied</h2>
        <p>You don't have permission to access this admin panel.</p>
        <p className="helper-text">Required: LISTER or ORACLE role</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {hasListerRole && (
        <div className="section">
          <h2 className="section-title">Create New Opportunity</h2>
          <form onSubmit={createOpportunity}>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Opportunity Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., AAVE v3 USDC Lending"
                  required
                />
              </div>

              <div className="form-group">
                <label>Chain</label>
                <select
                  value={formData.chain}
                  onChange={handleChainChange}
                >
                  <option value="base">Base (2s/block)</option>
                  <option value="hyperevm">HyperEVM (1s/block)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Seconds Per Block</label>
                <input
                  type="number"
                  value={formData.secsPerBlock}
                  onChange={(e) => setFormData(prev => ({ ...prev, secsPerBlock: e.target.value }))}
                  step="0.1"
                  required
                />
              </div>

              <div className="form-group">
                <label>Duration</label>
                <select
                  value={formData.duration}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                >
                  <option value="0">1 Day</option>
                  <option value="1">1 Week</option>
                  <option value="2">1 Month</option>
                </select>
              </div>

              <div className="form-group full-width">
                <label>Start Block</label>
                <div className="input-with-buttons">
                  <input
                    type="number"
                    value={formData.startBlock}
                    onChange={(e) => setFormData(prev => ({ ...prev, startBlock: e.target.value }))}
                    placeholder="Block number"
                    required
                  />
                  <button type="button" onClick={setToNow}>Now</button>
                  <button type="button" onClick={addHour}>+1h</button>
                  <button type="button" onClick={addDay}>+1d</button>
                </div>
                {currentBlock && <div className="helper-text">Current block: {currentBlock}</div>}
              </div>

              <div className="form-group">
                <label>End Block (calculated)</label>
                <input type="text" value={calculateEndBlock()} disabled />
              </div>

              <div className="form-group">
                <label>Starting APY (%)</label>
                <input
                  type="number"
                  value={formData.startApy}
                  onChange={(e) => setFormData(prev => ({ ...prev, startApy: e.target.value }))}
                  step="0.01"
                  placeholder="e.g., 40.50"
                  required
                />
              </div>

              <div className="form-group full-width">
                <label>Reference Transaction Hash</label>
                <input
                  type="text"
                  value={formData.refTxnHash}
                  onChange={(e) => setFormData(prev => ({ ...prev, refTxnHash: e.target.value }))}
                  placeholder="0x..."
                  required
                />
              </div>

              <div className="form-group full-width">
                <label>Thresholds (%)</label>
                <div className="thresholds-grid">
                  {['threshold1', 'threshold2', 'threshold3', 'threshold4', 'threshold5'].map((key, idx) => (
                    <input
                      key={key}
                      type="number"
                      value={formData[key]}
                      onChange={(e) => setFormData(prev => ({ ...prev, [key]: e.target.value }))}
                      step="0.01"
                      placeholder={`T${idx + 1}`}
                      required
                    />
                  ))}
                </div>
              </div>

              {formData.startApy && (
                <div className="form-group full-width">
                  <label>Preview - Betting Options</label>
                  <ThresholdsPreview
                    thresholds={{
                      threshold1: parseFloat(formData.threshold1) * 10000 || 0,
                      threshold2: parseFloat(formData.threshold2) * 10000 || 0,
                      threshold3: parseFloat(formData.threshold3) * 10000 || 0,
                      threshold4: parseFloat(formData.threshold4) * 10000 || 0,
                      threshold5: parseFloat(formData.threshold5) * 10000 || 0,
                    }}
                    startApy={parseFloat(formData.startApy) * 10000 || 0}
                    currentBlock={currentBlock}
                    startBlock={parseInt(formData.startBlock) || null}
                    endBlock={parseInt(calculateEndBlock()) || null}
                    secsPerBlock={parseFloat(formData.secsPerBlock) || 2}
                  />
                </div>
              )}
            </div>

            <button type="submit" className="btn" disabled={creating}>
              {creating ? 'Creating...' : 'Create Opportunity'}
            </button>

            {createMessage && (
              <div className={`message ${createMessage.type}`}>
                {createMessage.text}
              </div>
            )}
          </form>
        </div>
      )}

      <div className="section">
        <h2 className="section-title">{hasOracleRole ? 'Manage' : 'View'} Opportunities</h2>
        <div className="opportunities-list">
          {opportunities.length === 0 ? (
            <p className="empty">No opportunities yet.</p>
          ) : (
            opportunities.map(opp => (
              <OpportunityCard
                key={opp.id}
                opportunity={opp}
                currentBlock={currentBlock}
                hasOracleRole={hasOracleRole}
                finalApyInputs={finalApyInputs}
                setFinalApyInputs={setFinalApyInputs}
                resolvingId={resolvingId}
                voidingId={voidingId}
                onResolve={resolveOpportunity}
                onVoid={voidOpportunity}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function OpportunityCard({
  opportunity: opp,
  currentBlock,
  hasOracleRole,
  finalApyInputs,
  setFinalApyInputs,
  resolvingId,
  voidingId,
  onResolve,
  onVoid
}) {
  const status = getOpportunityStatus(opp, currentBlock);
  const canResolve = hasOracleRole && !opp.resolved && !opp.voided && status === 'awaiting_resolution';
  const canVoid = hasOracleRole && !opp.resolved && !opp.voided;

  return (
    <div className="opp-card">
      <div className="opp-header">
        <span className="opp-name">#{opp.id} - {opp.name}</span>
        <StatusBadge status={status} />
      </div>

      <div className="opp-details">
        <div className="opp-detail">
          <div className="detail-label">Duration</div>
          <div className="detail-value">{DURATIONS[Number(opp.durationEnum)]?.label}</div>
        </div>
        <div className="opp-detail">
          <div className="detail-label">Start APY</div>
          <div className="detail-value">{formatAPY(opp.startApy)}</div>
        </div>
        <div className="opp-detail">
          <div className="detail-label">Start Block</div>
          <div className="detail-value">{Number(opp.startBlock).toLocaleString()}</div>
        </div>
        <div className="opp-detail">
          <div className="detail-label">End Block</div>
          <div className="detail-value">{Number(opp.endBlock).toLocaleString()}</div>
        </div>
      </div>

      {opp.resolved && !opp.voided && (
        <div className="resolution-details">
          <span>Final APY: {formatAPY(opp.finalApy)}</span>
          <span>Winner: Option {opp.winningOption}</span>
          <span>Multiplier: {(Number(opp.rewardPerToken) / 1e18).toFixed(2)}x</span>
        </div>
      )}

      {(canResolve || canVoid) && (
        <div className="opp-actions">
          {canResolve && (
            <>
              <input
                type="number"
                placeholder="Final APY (%)"
                value={finalApyInputs[opp.id] || ''}
                onChange={(e) => setFinalApyInputs(prev => ({ ...prev, [opp.id]: e.target.value }))}
                step="0.01"
              />
              <button
                className="btn"
                onClick={() => onResolve(opp.id)}
                disabled={resolvingId === opp.id}
              >
                {resolvingId === opp.id ? 'Resolving...' : 'Resolve'}
              </button>
            </>
          )}
          {canVoid && (
            <button
              className="btn btn-danger"
              onClick={() => onVoid(opp.id)}
              disabled={voidingId === opp.id}
            >
              {voidingId === opp.id ? 'Voiding...' : 'Void'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ThresholdsPreview({ thresholds, startApy, currentBlock, startBlock, endBlock, secsPerBlock }) {
  const optionLabels = generateOptionLabels(thresholds, startApy);
  const startApyPercent = (startApy / 10000).toFixed(4);

  // Format time from seconds
  const formatTimeFromSeconds = (totalSeconds) => {
    if (totalSeconds < 0) return 'Already passed';

    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    if (days > 0) {
      return `in ${days}d ${hours}h`;
    } else if (hours > 0) {
      return `in ${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `in ${minutes}m`;
    }
    return 'now';
  };

  // Calculate time info for start/end blocks
  const getTimeInfo = (targetBlock) => {
    if (!currentBlock || !targetBlock || !secsPerBlock) return null;
    const blocksRemaining = targetBlock - currentBlock;
    const secondsRemaining = blocksRemaining * secsPerBlock;
    return formatTimeFromSeconds(secondsRemaining);
  };

  const startTimeInfo = getTimeInfo(startBlock);
  const endTimeInfo = getTimeInfo(endBlock);

  return (
    <div className="thresholds-preview">
      {(startTimeInfo || endTimeInfo) && (
        <div className="preview-times">
          {startTimeInfo && (
            <div className="preview-time-item">
              <span className="preview-time-label">Observation Starts:</span>
              <span className="preview-time-value">
                Block {startBlock?.toLocaleString()} <span className="preview-time-relative">({startTimeInfo})</span>
              </span>
            </div>
          )}
          {endTimeInfo && (
            <div className="preview-time-item">
              <span className="preview-time-label">Observation Ends:</span>
              <span className="preview-time-value">
                Block {endBlock?.toLocaleString()} <span className="preview-time-relative">({endTimeInfo})</span>
              </span>
            </div>
          )}
        </div>
      )}
      <div className="preview-grid">
        {optionLabels.slice(0, 3).map((option, index) => (
          <div key={index} className={`preview-option ${option.type}`}>
            <div className="preview-main">{option.main}</div>
            <div className="preview-sub">{option.sub}</div>
          </div>
        ))}

        <div className="preview-benchmark">
          <div className="preview-benchmark-apy">{startApyPercent}%</div>
          <div className="preview-benchmark-label">Benchmark</div>
        </div>

        {optionLabels.slice(3, 6).map((option, index) => (
          <div key={index + 3} className={`preview-option ${option.type}`}>
            <div className="preview-main">{option.main}</div>
            <div className="preview-sub">{option.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Admin;
